import React from 'react';
import {
  List,
  Datagrid,
  TextField,
  EditButton,
  CreateButton,
  FunctionField,
  ListProps,
  BulkDeleteButton,
  useListContext,
} from 'react-admin';
import { Box, Chip, Paper, Stack, Typography } from '@mui/material';
import DeleteWithConfirmButton from "../../../components/DeleteWithConfirmButton.tsx";

const ActionListHeader = () => {
  const { total } = useListContext();

  return (
    <Paper
      elevation={0}
      sx={{
        mb: 2,
        p: 2.5,
        borderRadius: '14px',
        border: '1px solid #e5e7eb',
        backgroundColor: '#ffffff',
      }}
    >
      <Stack direction="row" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={1.5}>
        <Box>
          <Typography variant="h6" fontWeight={800} sx={{ color: '#0f172a', mb: 0.5 }}>
            Action Management
          </Typography>
          <Typography variant="body2" sx={{ color: '#64748b' }}>
            Manage available actions under each menu.
          </Typography>
        </Box>
        <Stack direction="row" spacing={1} alignItems="center">
          <Chip label={`Total Actions: ${total ?? 0}`} sx={{ height: 32, fontWeight: 700, bgcolor: '#f1f5f9', color: '#334155' }} />
          <CreateButton
            label="New Action"
            sx={{
              px: 2,
              height: 34,
              borderRadius: '10px',
              fontWeight: 700,
              backgroundColor: '#0f172a',
              color: '#fff',
              '&:hover': { backgroundColor: '#111827' },
              '& .MuiButton-startIcon, & .MuiSvgIcon-root': { color: '#fff' },
            }}
          />
        </Stack>
      </Stack>
    </Paper>
  );
};

const ActionButtons = ({ record }: { record?: any }) => {
  if (!record) return null;

  return (
    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 1, width: '100%' }}>
      <EditButton record={record} sx={{ textTransform: 'capitalize', '& span': { mr: '2px' } }} />
      <DeleteWithConfirmButton record={record} resource="permissionActions" />
    </Box>
  );
};

const ActionList = (props: ListProps) => (
  <List
    sx={{ '& .RaList-content': { p: '0 !important' } }}
    empty={false}
    {...props}
    actions={false}
  >
    <ActionListHeader />
    <Paper elevation={0} sx={{ p: 0.75, borderRadius: '14px', overflow: 'visible', border: '1px solid #e5e7eb', boxShadow: '0 6px 18px rgba(15, 23, 42, 0.05)', bgcolor: '#fff' }}>
      <Datagrid
        rowClick={false}
        isRowSelectable={() => true}
        bulkActionButtons={<BulkDeleteButton label="Batch Delete" />}
        sx={{
          borderRadius: '10px',
          overflow: 'hidden',
          '& .RaDatagrid-table': { tableLayout: 'fixed' },
          '& .RaDatagrid-headerCell': { textAlign: 'center', bgcolor: '#f8fafc', fontWeight: 700, color: '#334155', py: 1.25 },
          '& .RaDatagrid-rowCell': { textAlign: 'center', py: 1.1, verticalAlign: 'middle' },
          '& .RaDatagrid-headerCell:first-of-type, & .RaDatagrid-rowCell:first-of-type': { pl: 2 },
          '& .RaDatagrid-headerCell:last-of-type, & .RaDatagrid-rowCell:last-of-type': { pr: 1.5 },
          '& .RaDatagrid-headerCell.MuiTableCell-paddingCheckbox': { pr: '25px !important' },
          '& .RaDatagrid-row:nth-of-type(even)': { bgcolor: '#fbfdff' },
          '& .RaDatagrid-row:hover': { bgcolor: '#f8fafc' },
        }}
      >
        <FunctionField
          label="Action ID"
          render={(record: any) => (
            <Chip size="small" label={`#${record?.id ?? '-'}`} sx={{ bgcolor: '#f1f5f9', color: '#334155', fontWeight: 700 }} />
          )}
        />
        <TextField source="menu.title" label="Menu Title" />
        <FunctionField
          label="Action Name"
          render={(record: any) => (
            <Chip size="small" label={record?.actionName || '-'} sx={{ fontWeight: 700, bgcolor: '#f1f5f9', color: '#334155' }} />
          )}
        />
        <TextField source="actionCode" label="Action Code" />
        <FunctionField label="Operations" render={(record: any) => <ActionButtons record={record} />} />
      </Datagrid>
    </Paper>
  </List>
);

export default ActionList;
